import React, { useState } from 'react';
import Card from '../UI/Card';
import { CONFIG } from '../../config';
import { Award, Calendar, Image as ImageIcon, X } from 'lucide-react';

/**
 * Certifications Section Component
 * 
 * Displays a grid of certifications/achievements.
 * Shows image, title, description, and year.
 * Includes a full-screen image viewer modal.
 */
const Certifications = () => {
    const [selectedImage, setSelectedImage] = useState(null);

    return (
        <section className="py-12 md:py-20 px-4 sm:px-10 animate-in fade-in duration-500">
            <div className="max-w-7xl mx-auto">
                <div className="mb-12 animate-in slide-in-from-left-4 duration-700">
                    <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-3">
                        <Award className="text-purple-600 dark:text-purple-400" size={32} />
                        Certifications & Achievements
                    </h2>
                    <p className="text-slate-600 dark:text-slate-400 break-words pr-2">My professional certifications and learning milestones.</p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {CONFIG.certifications && CONFIG.certifications.map((cert, index) => (
                        <Card 
                            key={cert.id} 
                            className="flex flex-col h-full animate-in fade-in slide-in-from-bottom-8 fill-mode-both"
                            style={{ animationDelay: `${index * 100}ms` }}
                        >
                            {cert.image && (
                                <div 
                                    className="h-48 mb-6 rounded-xl overflow-hidden cursor-pointer group relative border border-gray-200 dark:border-white/10 bg-slate-100 dark:bg-slate-800"
                                    onClick={() => setSelectedImage(cert.image)}
                                >
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity z-10 flex items-center justify-center">
                                        <ImageIcon size={32} className="text-white drop-shadow-md transform scale-50 group-hover:scale-100 transition-transform duration-300" />
                                    </div>
                                    <img 
                                        src={cert.image} 
                                        alt={cert.title} 
                                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                        onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Certification' }}
                                    />
                                </div>
                            )}

                            <div className="flex-1 flex flex-col">
                                <div className="flex justify-between items-start mb-2 gap-4">
                                    <h3 className="text-xl font-bold text-slate-900 dark:text-white line-clamp-2">
                                        {cert.title}
                                    </h3>
                                    <span className="shrink-0 flex items-center gap-1.5 px-3 py-1 bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 rounded-full text-xs font-semibold shadow-sm dark:shadow-none">
                                        <Calendar size={14} />
                                        {cert.year}
                                    </span>
                                </div>
                                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mt-2">
                                    {cert.description}
                                </p>
                            </div>
                        </Card>
                    ))}
                </div>

                {/* Full Image Modal */}
                {selectedImage && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-300">
                        <div 
                            className="absolute inset-0 bg-slate-900/90 backdrop-blur-md"
                            onClick={() => setSelectedImage(null)}
                        ></div>
                        <div className="relative w-full max-w-5xl rounded-2xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
                            <button
                                onClick={() => setSelectedImage(null)}
                                className="absolute top-4 right-4 z-20 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-md transition-colors"
                            >
                                <X size={24} />
                            </button>
                            <img 
                                src={selectedImage} 
                                alt="Certification Full Screen" 
                                className="w-full h-auto max-h-[85vh] object-contain bg-slate-900"
                                onError={(e) => { e.target.src = 'https://via.placeholder.com/800x600?text=Certification' }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default Certifications;
